import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';
import { showErrorToast, showSuccessToast, showInfoToast } from '@/lib/toast-utils';

export interface CartItem {
  id: string;
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  imageUrl?: string;
  orderLineType?: string;
}

export interface Cart {
  id: string;
  secretId?: string;
  totalItems: number;
  totalAmount: number;
  subtotalAmount: number;
  shippingFee: number;
  paymentFee: number;
  currencyCode: string;
  items: CartItem[];
  lastModified: string;
  name?: string;
  shippingMethod?: {
    id: string;
    name: string;
    description: string;
  };
  paymentMethod?: {
    id: string;
    name: string;
    description: string;
  };
}

export interface CartSummary {
  id: string;
  secretId: string;
  name: string;
  totalItems: number;
  totalAmount: number;
  currencyCode: string;
  lastModified: string;
}

export interface FetchSavedCartsParams {
  dayFilter?: number;
  pageSize?: number;
  page?: number;
}

interface CartContextType {
  cart: Cart | null;
  totalItems: number;
  activeCartSecretId: string | null;
  isLoading: boolean;
  savedCarts: CartSummary[];
  totalPages: number;
  currentPage: number;
  refreshKey: number;
  fetchActiveCart: () => Promise<void>;
  setActiveCart: (secretId: string) => Promise<void>;
  createNewCart: () => Promise<void>;
  fetchSavedCarts: (params?: FetchSavedCartsParams) => Promise<void>;
  deleteCart: (secretId: string) => Promise<void>;
  updateCartName: (cartSecret: string, displayName: string) => Promise<boolean>;
  refreshCart: () => Promise<void>;
  addToCart: (productId: string, quantity: number, unitId?: string) => Promise<boolean>;
  updateCartItemQuantity: (orderLineId: string, quantity: number) => Promise<boolean>;
  removeFromCart: (orderLineId: string) => Promise<boolean>;
  setShippingMethod: (shippingId: string) => Promise<boolean>;
  setPaymentMethod: (paymentId: string) => Promise<boolean>;
  createOrder: () => Promise<{ success: boolean; orderId?: string }>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [cart, setCart] = useState<Cart | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [savedCarts, setSavedCarts] = useState<CartSummary[]>([]);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const { makeAuthenticatedCall } = useAuthenticatedApi();

  // Derive values directly from cart to ensure consistency
  const totalItems = cart?.totalItems || 0;
  const activeCartSecretId = cart?.secretId || null;
  
  console.log('CartContext: Current cart state - totalItems:', totalItems, 'secretId:', activeCartSecretId, 'refreshKey:', refreshKey);

  const fetchActiveCart = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('fetchActiveCart: Starting...');
      const { data, error } = await makeAuthenticatedCall('get-active-cart');
      
      if (error) {
        console.log('fetchActiveCart: Error fetching active cart:', error);
        setCart(null);
        return;
      }

      if (data && typeof data === 'object' && 'cart' in data && data.cart) {
        const fetchedCart = data.cart as Cart;
        console.log('fetchActiveCart: Cart object:', fetchedCart);
        console.log('fetchActiveCart: Setting cart with totalItems:', fetchedCart.totalItems);
        setCart(fetchedCart);
        setRefreshKey(prev => prev + 1);
      } else {
        console.log('fetchActiveCart: No active cart found'); 
        setCart(null);
      }
    } catch (err) {
      console.error('fetchActiveCart: Exception:', err);
      setCart(null);
      showErrorToast('Failed to fetch cart information');
    } finally {
      setIsLoading(false);
    }
  }, [makeAuthenticatedCall]);

  const handleNoActiveCart = useCallback(async () => {
    try {
      console.log('No active cart found, checking for existing carts...');
      
      const { data: listData, error: listError } = await makeAuthenticatedCall('list-carts');
      
      if (listError || !listData || typeof listData !== 'object' || !('carts' in listData)) {
        console.log('No existing carts, creating new cart...');
        const { data, error } = await makeAuthenticatedCall('create-cart', {});
        if (!error && data && typeof data === 'object' && 'cart' in data && data.cart) {
          setCart(data.cart as Cart);
        }
        return;
      }

      const carts = listData.carts as CartSummary[];
      
      if (carts.length === 0) {
        console.log('No saved carts with items, creating new cart...');
        const { data, error } = await makeAuthenticatedCall('create-cart', {});
        if (!error && data && typeof data === 'object' && 'cart' in data && data.cart) {
          setCart(data.cart as Cart);
        }
      } else if (carts.length === 1) {
        console.log('Found exactly one cart, setting as active...');
        const { error } = await makeAuthenticatedCall('set-active-cart', { secret: carts[0].secretId });
        if (!error) {
          await fetchActiveCart();
        }
      } else {
        console.log(`Found ${carts.length} carts, user needs to select one`);
        setSavedCarts(carts);
        setCart(null);
      }
    } catch (err) {
      console.error('Error handling no active cart:', err);
      const { data, error } = await makeAuthenticatedCall('create-cart', {});
      if (!error && data && typeof data === 'object' && 'cart' in data && data.cart) {
        setCart(data.cart as Cart);
      }
    }
  }, [makeAuthenticatedCall, fetchActiveCart]);

  const fetchSavedCarts = useCallback(async (params: FetchSavedCartsParams = {}) => {
    try {
      console.log('fetchSavedCarts: Starting to fetch all saved carts with params:', params);
      
      const { dayFilter = 30, pageSize = 10, page = 1 } = params;
      let allCarts: CartSummary[] = [];
      let totalPagesCount = 0;
      
      const filterDate = dayFilter > 0 ? new Date(Date.now() - dayFilter * 24 * 60 * 60 * 1000).toISOString() : null;
      
      const { data, error } = await makeAuthenticatedCall('list-carts', {
        page,
        pageSize,
        ...(filterDate && { modifiedAfter: filterDate })
      });
      
      console.log(`fetchSavedCarts: Response:`, { data, error });
      
      if (error) {
        console.error('Error fetching saved carts:', error);
        return;
      }

      if (data && typeof data === 'object' && 'carts' in data) {
        const cartsData = data.carts as CartSummary[];
        allCarts = cartsData;
        
        totalPagesCount = (data as any).pageCount || 1;
        setCurrentPage((data as any).currentPage || page);
      }
      
      console.log('fetchSavedCarts: Carts fetched:', allCarts.length, 'total carts, pages:', totalPagesCount);
      setSavedCarts(allCarts);
      setTotalPages(totalPagesCount);
    } catch (err) {
      console.error('Error fetching saved carts:', err);
    }
  }, [makeAuthenticatedCall]);

  const setActiveCart = useCallback(async (secretId: string) => {
    try {
      setIsLoading(true);
      console.log('setActiveCart called with secretId:', secretId);
      
      const { data, error } = await makeAuthenticatedCall('set-active-cart', { secret: secretId });
      
      console.log('set-active-cart response:', { data, error });
      
      if (error) {
        console.error('set-active-cart returned error:', error);
        throw new Error(error);
      }

      console.log('set-active-cart succeeded, fetching new cart...');
      
      setIsLoading(false);
      await fetchActiveCart();
      
      showSuccessToast('Your selected cart is now active.', 'Cart Activated');
    } catch (err) {
      console.error('Error setting active cart:', err);
      showErrorToast('Failed to activate the selected cart');
    } finally {
      setIsLoading(false);
    }
  }, [makeAuthenticatedCall, fetchActiveCart]);

  const createNewCart = useCallback(async () => {
    try {
      console.log('Creating new cart...');
      const { data, error } = await makeAuthenticatedCall('create-cart', {});
      
      if (error) {
        throw new Error(error);
      }

      if (data && typeof data === 'object' && 'cart' in data && data.cart) {
        const newCart = data.cart as Cart;
        
        if (newCart.secretId) {
          console.log('Setting new cart as active:', newCart.secretId);
          const { error: setActiveError } = await makeAuthenticatedCall('set-active-cart', { 
            secret: newCart.secretId 
          });
          if (setActiveError) {
            console.error('Error setting new cart as active:', setActiveError);
          } else {
            console.log('Successfully set new cart as active');
          }
        }
        
        await fetchActiveCart();
        await fetchSavedCarts({ dayFilter: 30, pageSize: 10, page: 1 });
        
        showSuccessToast('A new shopping cart has been created and set as active.', 'Cart Created');
      }
    } catch (err) {
      console.error('Error creating cart:', err);
      showErrorToast('Failed to create a new cart');
    }
  }, [makeAuthenticatedCall, fetchActiveCart, fetchSavedCarts]);

  const deleteCart = useCallback(async (secretId: string) => {
    try {
      console.log('Deleting cart with secretId:', secretId);
      const { data, error } = await makeAuthenticatedCall('delete-cart', { secretId });
      
      if (error) {
        console.error('Error deleting cart:', error);
        showErrorToast('Failed to delete cart');
        return;
      }

      console.log('Cart deleted successfully:', data);
      
      const wasActiveCart = cart?.secretId === secretId;
      
      setSavedCarts(prev => {
        const updatedCarts = prev.filter(cart => cart.secretId !== secretId);
        
        if (wasActiveCart && updatedCarts.length > 0) {
          const mostRecentCart = updatedCarts.sort((a, b) => 
            new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
          )[0];
          
          console.log('Setting most recent cart as active:', mostRecentCart.secretId);
          setTimeout(() => {
            setActiveCart(mostRecentCart.secretId);
          }, 100);
        } else if (wasActiveCart) {
          setCart(null);
        }
        
        return updatedCarts;
      });
      
      showSuccessToast(
        wasActiveCart 
          ? 'The cart has been deleted and the most recent cart is now active.'
          : 'The cart has been successfully deleted.',
        'Cart Deleted'
      );
    } catch (err) {
      console.error('Error deleting cart:', err);
      showErrorToast('Failed to delete cart');
    }
  }, [makeAuthenticatedCall, cart?.secretId, setActiveCart]);

  const updateCartName = useCallback(async (cartSecret: string, displayName: string): Promise<boolean> => {
    try {
      console.log('Updating cart name:', { cartSecret, displayName });
      const { data, error } = await makeAuthenticatedCall('update-cart-name', { 
        cartSecret,
        displayName 
      });
      
      if (error) {
        console.error('Error updating cart name:', error);
        showErrorToast('Failed to update cart name');
        return false;
      }

      console.log('Cart name updated successfully:', data);
      
      await fetchActiveCart();
      
      showSuccessToast('Cart name has been updated successfully.', 'Cart Updated');
      
      return true;
    } catch (err) {
      console.error('Error updating cart name:', err);
      showErrorToast('Failed to update cart name');
      return false;
    }
  }, [makeAuthenticatedCall, fetchActiveCart]);

  // Cart initialization is now handled by NavigationContext (T2 phase)

  const addToCart = useCallback(async (productId: string, quantity: number, unitId?: string): Promise<boolean> => {
    if (!cart?.secretId) {
      showErrorToast('No active cart found');
      return false;
    }

    try {
      // Check if product already exists in cart
      const existingItem = cart.items?.find(item => item.productId === productId);

      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        
        const { data, error } = await makeAuthenticatedCall('update-cart-item', {
          cartSecret: cart.secretId,
          orderLineId: existingItem.id,
          quantity: newQuantity,
        });

        if (error) {
          throw new Error(error);
        }

        await fetchActiveCart();
        showSuccessToast(`Quantity updated to ${newQuantity}`, 'Cart Updated');
        return true;
      }

      const { data, error } = await makeAuthenticatedCall('add-to-cart', {
        cartSecret: cart.secretId,
        productNumber: productId,
        quantity,
        unitId,
      });

      if (error) {
        throw new Error(error);
      }

      await fetchActiveCart();
      showSuccessToast('Item has been added to your cart', 'Added to Cart');
      return true;
    } catch (err) {
      console.error('Error adding to cart:', err);
      showErrorToast('Failed to add item to cart');
      return false;
    }
  }, [cart?.secretId, cart?.items, makeAuthenticatedCall, fetchActiveCart]);

  const updateCartItemQuantity = useCallback(async (orderLineId: string, quantity: number): Promise<boolean> => {
    if (!cart?.secretId) {
      showErrorToast('No active cart found');
      return false;
    }

    // Optimistic update
    const previousCart = cart;
    const updatedItems = cart.items.map(item => 
      item.id === orderLineId 
        ? { ...item, quantity, totalPrice: item.unitPrice * quantity }
        : item
    );
    
    const newTotalItems = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
    const newSubtotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const newTotal = newSubtotal + cart.shippingFee + cart.paymentFee;
    
    setCart({
      ...cart,
      items: updatedItems,
      totalItems: newTotalItems,
      subtotalAmount: newSubtotal,
      totalAmount: newTotal,
    });

    try {
      const { data, error } = await makeAuthenticatedCall('update-cart-item', {
        cartSecret: cart.secretId,
        orderLineId,
        quantity,
      });

      if (error) {
        throw new Error(error);
      }

      return true;
    } catch (err) {
      console.error('Error updating cart item:', err);
      setCart(previousCart);
      showErrorToast('Failed to update item quantity');
      return false;
    }
  }, [cart, makeAuthenticatedCall]);

  const removeFromCart = useCallback(async (orderLineId: string): Promise<boolean> => {
    if (!cart?.secretId) {
      showErrorToast('No active cart found');
      return false;
    }

    // Optimistic update
    const previousCart = cart;
    const updatedItems = cart.items.filter(item => item.id !== orderLineId);
    
    const newTotalItems = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
    const newSubtotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);
    const newTotal = newSubtotal + cart.shippingFee + cart.paymentFee;
    
    setCart({
      ...cart,
      items: updatedItems,
      totalItems: newTotalItems,
      subtotalAmount: newSubtotal,
      totalAmount: newTotal,
    });

    try {
      const { data, error } = await makeAuthenticatedCall('remove-from-cart', {
        cartSecret: cart.secretId,
        orderLineId,
      });

      if (error) {
        throw new Error(error);
      }

      showSuccessToast('Item has been removed from your cart', 'Item Removed');
      return true;
    } catch (err) {
      console.error('Error removing from cart:', err);
      setCart(previousCart);
      showErrorToast('Failed to remove item from cart');
      return false;
    }
  }, [cart, makeAuthenticatedCall]);

  const setShippingMethod = useCallback(async (shippingId: string): Promise<boolean> => {
    if (!cart?.secretId) {
      showErrorToast('No active cart found');
      return false;
    }

    const previousCart = cart;

    try {
      const { data, error } = await makeAuthenticatedCall('set-shipping-method', {
        cartSecret: cart.secretId,
        shippingId,
      });

      if (error) {
        throw new Error(error);
      }

      if (data && typeof data === 'object' && 'cart' in data && data.cart) {
        const apiResponse = data as { success: boolean; cart: Cart };
        setCart(apiResponse.cart);
        setRefreshKey(prev => prev + 1);
      }
      
      return true;
    } catch (err) {
      console.error('Error setting shipping method:', err);
      setCart(previousCart);
      showErrorToast('Failed to set shipping method');
      return false;
    }
  }, [cart, makeAuthenticatedCall]);

  const setPaymentMethod = useCallback(async (paymentId: string): Promise<boolean> => {
    if (!cart?.secretId) {
      showErrorToast('No active cart found');
      return false;
    }

    const previousCart = cart;

    try {
      const { data, error } = await makeAuthenticatedCall('set-payment-method', {
        cartSecret: cart.secretId,
        paymentId,
      });

      if (error) {
        throw new Error(error);
      }

      if (data && typeof data === 'object' && 'cart' in data && data.cart) {
        const apiResponse = data as { success: boolean; cart: Cart };
        setCart(apiResponse.cart);
        setRefreshKey(prev => prev + 1);
      }
      
      return true;
    } catch (err) {
      console.error('Error setting payment method:', err);
      setCart(previousCart);
      showErrorToast('Failed to set payment method');
      return false;
    }
  }, [cart, makeAuthenticatedCall]);

  const createOrder = useCallback(async (): Promise<{ success: boolean; orderId?: string }> => {
    if (!cart?.secretId) {
      showErrorToast('No active cart found');
      return { success: false };
    }

    try {
      const { data, error } = await makeAuthenticatedCall('create-order', {
        cartSecret: cart.secretId,
      });

      if (error) {
        throw new Error(error);
      }

      const orderData = data as { order?: any };
      const orderId = orderData?.order?.id;

      showSuccessToast(`Order #${orderId || 'N/A'} has been placed successfully`, 'Order Created');

      await createNewCart();

      return { success: true, orderId };
    } catch (err) {
      console.error('Error creating order:', err);
      showErrorToast('Failed to create order');
      return { success: false };
    }
  }, [cart?.secretId, makeAuthenticatedCall, createNewCart]);

  const value: CartContextType = {
    cart,
    totalItems,
    activeCartSecretId,
    isLoading,
    savedCarts,
    totalPages,
    currentPage,
    refreshKey,
    fetchActiveCart,
    setActiveCart,
    createNewCart,
    fetchSavedCarts,
    deleteCart,
    updateCartName,
    refreshCart: fetchActiveCart,
    addToCart,
    updateCartItemQuantity,
    removeFromCart,
    setShippingMethod,
    setPaymentMethod,
    createOrder,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
